/**
 * useAsyncAction - Hook for handling async button actions with loading state.
 *
 * Consolidates the async loading state pattern that was previously
 * duplicated in:
 * - unified-view/ApprovedLinkBadge.tsx
 * - unified-view/SuggestedLinkBadge.tsx
 */

import { useCallback, useState } from 'react';

interface UseAsyncActionResult {
  /** Whether the action is currently executing */
  isLoading: boolean;
  /**
   * Execute the async action with automatic loading state management.
   * Prevents double-clicks and handles errors gracefully.
   */
  execute: (e?: React.MouseEvent) => Promise<void>;
}

/**
 * Hook for handling async button actions with loading state.
 *
 * Automatically manages loading state and prevents double-clicks.
 * Stops event propagation to prevent parent handlers from firing.
 *
 * @param action - The async or sync function to execute (sync functions are wrapped)
 * @param disabled - Optional external disabled state
 * @returns Object with isLoading state and execute handler
 *
 * @example
 * const { isLoading, execute } = useAsyncAction(async () => {
 *   await api.approveLink(linkId);
 * });
 *
 * return (
 *   <button onClick={execute} disabled={isLoading}>
 *     {isLoading ? <Spinner /> : 'Approve'}
 *   </button>
 * );
 */
export function useAsyncAction(
  action: () => void | Promise<void>,
  disabled = false,
): UseAsyncActionResult {
  const [isLoading, setIsLoading] = useState(false);

  const execute = useCallback(
    async (e?: React.MouseEvent) => {
      // Stop propagation to prevent parent handlers
      e?.stopPropagation();

      // Prevent execution if already loading or disabled
      if (isLoading || disabled) return;

      setIsLoading(true);
      try {
        await action();
      } finally {
        setIsLoading(false);
      }
    },
    [action, isLoading, disabled],
  );

  return { isLoading, execute };
}

export default useAsyncAction;
